import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import gifshot from 'gifshot';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { mediaUrl } from '../utils/blogUtils';
import {
  buildPositionBasedLayout,
  assignPlayersToFormationSlots,
  getEffectivePosition,
  buildVerticalFormationTemplate,
} from '../utils/formationPlacement';
import './SoccerFieldVisualization.css';

const getPositionAbbreviation = (position) => {
  if (!position) return 'FLX';
  const abbrev = {
    GOALKEEPER: 'GK',
    DEFENDER: 'DEF',
    MIDFIELDER: 'MID',
    FORWARD: 'FWD',
    WINGER: 'WNG',
    FLEXIBLE: 'FLX',
  };
  return abbrev[position] || 'FLX';
};

const playerPositionLabel = (player) =>
  getPositionAbbreviation(getEffectivePosition(player));

/** Display name on field markers — e.g. "John Smith" → "J. Smith" */
const getPlayerShortName = (name) => {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].length > 9 ? `${parts[0].slice(0, 8)}…` : parts[0];
  }
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first.charAt(0).toUpperCase()}. ${last}`;
};

const MIN_MARKER_GAP_X = 12;
const MIN_MARKER_GAP_Y = 22;

const isVerticalLayout = (layout) => layout === 'vertical' || layout === 'vertical-matchup';

/** Mirror vertical coords when the goal is at the top of the pitch */
const mirrorVerticalY = (y) => 100 - y;

const getPlayerInitials = (name) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

/** Nudge overlapping markers apart on the same half-pitch */
const resolveOverlaps = (positions) => {
  const result = positions.map((p) => ({ ...p }));
  for (let pass = 0; pass < 4; pass += 1) {
    for (let i = 0; i < result.length; i += 1) {
      for (let j = i + 1; j < result.length; j += 1) {
        const dx = result[j].x - result[i].x;
        const dy = result[j].y - result[i].y;
        if (Math.abs(dx) < MIN_MARKER_GAP_X && Math.abs(dy) < MIN_MARKER_GAP_Y) {
          const nudgeY = dy >= 0 ? MIN_MARKER_GAP_Y : -MIN_MARKER_GAP_Y;
          result[j].y = Math.max(8, Math.min(92, result[i].y + nudgeY));
          if (Math.abs(result[j].x - result[i].x) < MIN_MARKER_GAP_X) {
            result[j].x = Math.max(6, Math.min(94, result[i].x + MIN_MARKER_GAP_X));
          }
        }
      }
    }
  }
  return result;
};

/** Map legacy shared-pitch coordinates to a single half-pitch (goal on left) */
const normalizeStoredX = (x) => {
  if (x == null) return 50;
  if (x <= 48) return Math.max(6, Math.min(94, x * 2));
  if (x >= 52) return Math.max(6, Math.min(94, 100 - x));
  return Math.max(6, Math.min(94, x));
};

/** Vertical half-pitch: goal at bottom (high y), attack toward top (low y) */
const VERTICAL_FORMATIONS = {
  '4-4-2': {
    GOALKEEPER: [{ x: 50, y: 90 }],
    DEFENDER: [
      { x: 15, y: 74 },
      { x: 35, y: 74 },
      { x: 65, y: 74 },
      { x: 85, y: 74 },
    ],
    MIDFIELDER: [
      { x: 15, y: 52 },
      { x: 35, y: 52 },
      { x: 65, y: 52 },
      { x: 85, y: 52 },
    ],
    FORWARD: [
      { x: 38, y: 26 },
      { x: 62, y: 26 },
    ],
    WINGER: [],
    FLEXIBLE: [],
  },
  '4-3-3': {
    GOALKEEPER: [{ x: 50, y: 90 }],
    DEFENDER: [
      { x: 15, y: 74 },
      { x: 35, y: 74 },
      { x: 65, y: 74 },
      { x: 85, y: 74 },
    ],
    MIDFIELDER: [
      { x: 25, y: 52 },
      { x: 50, y: 52 },
      { x: 75, y: 52 },
    ],
    FORWARD: [
      { x: 20, y: 26 },
      { x: 50, y: 26 },
      { x: 80, y: 26 },
    ],
    WINGER: [],
    FLEXIBLE: [],
  },
  '3-5-2': {
    GOALKEEPER: [{ x: 50, y: 90 }],
    DEFENDER: [
      { x: 25, y: 74 },
      { x: 50, y: 74 },
      { x: 75, y: 74 },
    ],
    MIDFIELDER: [
      { x: 10, y: 52 },
      { x: 30, y: 52 },
      { x: 50, y: 52 },
      { x: 70, y: 52 },
      { x: 90, y: 52 },
    ],
    FORWARD: [
      { x: 35, y: 26 },
      { x: 65, y: 26 },
    ],
    WINGER: [],
    FLEXIBLE: [],
  },
  '4-2-3-1': {
    GOALKEEPER: [{ x: 50, y: 90 }],
    DEFENDER: [
      { x: 15, y: 74 },
      { x: 35, y: 74 },
      { x: 65, y: 74 },
      { x: 85, y: 74 },
    ],
    MIDFIELDER: [
      { x: 35, y: 62 },
      { x: 65, y: 62 },
    ],
    FORWARD: [{ x: 50, y: 26 }],
    WINGER: [
      { x: 18, y: 42 },
      { x: 82, y: 42 },
    ],
    FLEXIBLE: [],
  },
  '3-4-3': {
    GOALKEEPER: [{ x: 50, y: 90 }],
    DEFENDER: [
      { x: 25, y: 74 },
      { x: 50, y: 74 },
      { x: 75, y: 74 },
    ],
    MIDFIELDER: [
      { x: 15, y: 52 },
      { x: 35, y: 52 },
      { x: 65, y: 52 },
      { x: 85, y: 52 },
    ],
    FORWARD: [
      { x: 20, y: 26 },
      { x: 50, y: 26 },
      { x: 80, y: 26 },
    ],
    WINGER: [],
    FLEXIBLE: [],
  },
};

const clampCoord = (value, min, max) => Math.max(min, Math.min(max, value));

const applyVerticalOrientation = (positions, goalAtTop) => {
  if (!goalAtTop) return positions;
  return positions.map((pos) => ({
    ...pos,
    y: mirrorVerticalY(pos.y),
  }));
};

const SoccerFieldVisualization = ({
  teams,
  practice,
  readOnly = false,
  initialShowField = false,
  layout = 'stacked',
  alwaysVisible = false,
  showPlayerPhotos = false,
  getPlayerProfileLink = null,
  zoomableTeams = false,
}) => {
  const { user, isAdmin } = useAuth();
  const [selectedFormations, setSelectedFormations] = useState({});
  const [showVisualization, setShowVisualization] = useState(initialShowField || alwaysVisible);
  const [savingFormation, setSavingFormation] = useState(false);
  const [customPositions, setCustomPositions] = useState({}); // Store custom dragged positions
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [positionUpdateCounter, setPositionUpdateCounter] = useState(0);
  const [gifGenerating, setGifGenerating] = useState(false);
  const [imageDownloading, setImageDownloading] = useState(false);
  const [positionsSaved, setPositionsSaved] = useState(false);
  const [sharingImage, setSharingImage] = useState(false);
  const [zoomedDivisionIndex, setZoomedDivisionIndex] = useState(null);
  const fieldCaptureRef = useRef(null);

  const POSITIONS_STORAGE_KEY = teams?.id ? `formation-positions-${teams.id}` : null;

  // Initialize formations from teams data if available
  useEffect(() => {
    if (teams && teams.divisions) {
      const initialFormations = {};
      teams.divisions.forEach((division, index) => {
        if (division.formation) {
          initialFormations[index.toString()] = division.formation;
        }
      });
      setSelectedFormations(initialFormations);
    }
  }, [teams]);

  // Load saved custom positions from backend or localStorage when team is set
  useEffect(() => {
    if (!teams) return;

    if (teams.customFieldPositions && Object.keys(teams.customFieldPositions).length > 0) {
      setCustomPositions(teams.customFieldPositions);
      return;
    }

    if (!POSITIONS_STORAGE_KEY) return;
    try {
      const stored = localStorage.getItem(POSITIONS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') setCustomPositions(parsed);
      }
    } catch (e) {
      console.warn('Could not load saved positions:', e);
    }
  }, [teams, POSITIONS_STORAGE_KEY]);

  // Half-pitch formations: goal on left (low x), attack toward right (high x)
  const FORMATIONS = {
    '4-4-2': {
      GOALKEEPER: [{ x: 10, y: 50 }],
      DEFENDER: [
        { x: 22, y: 16 },
        { x: 22, y: 38 },
        { x: 22, y: 62 },
        { x: 22, y: 84 },
      ],
      MIDFIELDER: [
        { x: 48, y: 16 },
        { x: 48, y: 38 },
        { x: 48, y: 62 },
        { x: 48, y: 84 },
      ],
      FORWARD: [
        { x: 78, y: 40 },
        { x: 78, y: 60 },
      ],
      WINGER: [],
      FLEXIBLE: [],
    },
    '4-3-3': {
      GOALKEEPER: [{ x: 10, y: 50 }],
      DEFENDER: [
        { x: 22, y: 16 },
        { x: 22, y: 38 },
        { x: 22, y: 62 },
        { x: 22, y: 84 },
      ],
      MIDFIELDER: [
        { x: 48, y: 28 },
        { x: 48, y: 50 },
        { x: 48, y: 72 },
      ],
      FORWARD: [
        { x: 80, y: 28 },
        { x: 80, y: 50 },
        { x: 80, y: 72 },
      ],
      WINGER: [],
      FLEXIBLE: [],
    },
    '3-5-2': {
      GOALKEEPER: [{ x: 10, y: 50 }],
      DEFENDER: [
        { x: 22, y: 28 },
        { x: 22, y: 50 },
        { x: 22, y: 72 },
      ],
      MIDFIELDER: [
        { x: 48, y: 12 },
        { x: 48, y: 30 },
        { x: 48, y: 50 },
        { x: 48, y: 70 },
        { x: 48, y: 88 },
      ],
      FORWARD: [
        { x: 78, y: 38 },
        { x: 78, y: 62 },
      ],
      WINGER: [],
      FLEXIBLE: [],
    },
    '4-2-3-1': {
      GOALKEEPER: [{ x: 10, y: 50 }],
      DEFENDER: [
        { x: 22, y: 16 },
        { x: 22, y: 38 },
        { x: 22, y: 62 },
        { x: 22, y: 84 },
      ],
      MIDFIELDER: [
        { x: 44, y: 38 },
        { x: 44, y: 62 },
      ],
      FORWARD: [{ x: 82, y: 50 }],
      WINGER: [
        { x: 64, y: 22 },
        { x: 64, y: 78 },
      ],
      FLEXIBLE: [],
    },
    '3-4-3': {
      GOALKEEPER: [{ x: 10, y: 50 }],
      DEFENDER: [
        { x: 22, y: 28 },
        { x: 22, y: 50 },
        { x: 22, y: 72 },
      ],
      MIDFIELDER: [
        { x: 48, y: 16 },
        { x: 48, y: 38 },
        { x: 48, y: 62 },
        { x: 48, y: 84 },
      ],
      FORWARD: [
        { x: 80, y: 28 },
        { x: 80, y: 50 },
        { x: 80, y: 72 },
      ],
      WINGER: [],
      FLEXIBLE: [],
    },
  };

  // Count players by position
  const countPlayersByPosition = (players) => {
    const counts = {
      GOALKEEPER: 0,
      DEFENDER: 0,
      MIDFIELDER: 0,
      FORWARD: 0,
      WINGER: 0,
      FLEXIBLE: 0
    };
    
    players.forEach(player => {
      const position = player.position || 'FLEXIBLE';
      counts[position] = (counts[position] || 0) + 1;
    });
    
    return counts;
  };

  // Suggest formations based on number of players
  const suggestFormationsByPlayerCount = (playerCount) => {
    const suggestions = [];
    
    if (playerCount === 5) {
      suggestions.push({ formation: '1-2-1', description: '1 GK, 2 DEF, 1 MID, 1 FWD' });
    } else if (playerCount === 6) {
      suggestions.push({ formation: '1-2-2', description: '1 GK, 2 DEF, 2 MID, 1 FWD' });
      suggestions.push({ formation: '1-3-1', description: '1 GK, 3 DEF, 1 MID, 1 FWD' });
    } else if (playerCount === 7) {
      suggestions.push({ formation: '1-3-2', description: '1 GK, 3 DEF, 2 MID, 1 FWD' });
      suggestions.push({ formation: '1-2-3', description: '1 GK, 2 DEF, 2 MID, 2 FWD' });
    } else if (playerCount === 8) {
      suggestions.push({ formation: '1-3-3', description: '1 GK, 3 DEF, 3 MID, 1 FWD' });
      suggestions.push({ formation: '1-2-4', description: '1 GK, 2 DEF, 2 MID, 3 FWD' });
    } else if (playerCount === 9) {
      suggestions.push({ formation: '1-3-4', description: '1 GK, 3 DEF, 3 MID, 2 FWD' });
      suggestions.push({ formation: '1-4-3', description: '1 GK, 4 DEF, 2 MID, 2 FWD' });
    } else if (playerCount === 10) {
      suggestions.push({ formation: '1-4-4', description: '1 GK, 4 DEF, 3 MID, 2 FWD' });
      suggestions.push({ formation: '1-3-5', description: '1 GK, 3 DEF, 4 MID, 2 FWD' });
    } else if (playerCount >= 11) {
      suggestions.push({ formation: '4-4-2', description: '1 GK, 4 DEF, 4 MID, 2 FWD (Classic)' });
      suggestions.push({ formation: '4-3-3', description: '1 GK, 4 DEF, 3 MID, 3 FWD (Attacking)' });
      suggestions.push({ formation: '3-5-2', description: '1 GK, 3 DEF, 5 MID, 2 FWD (Wing Play)' });
      suggestions.push({ formation: '4-2-3-1', description: '1 GK, 4 DEF, 2 MID, 3 AM, 1 FWD (Defensive)' });
      suggestions.push({ formation: '3-4-3', description: '1 GK, 3 DEF, 4 MID, 3 FWD (Wide)' });
    }
    
    return suggestions;
  };

  // Auto-detect formation based on player positions
  const detectFormation = (players) => {
    const counts = countPlayersByPosition(players);
    const gk = counts.GOALKEEPER || 0;
    const def = counts.DEFENDER || 0;
    const mid = counts.MIDFIELDER || 0;
    const fwd = counts.FORWARD || 0;
    const wing = counts.WINGER || 0;

    // Try to match to standard formations
    // 4-4-2: 1 GK, 4 DEF, 4 MID, 2 FWD
    if (gk === 1 && def === 4 && mid === 4 && fwd === 2) {
      return '4-4-2';
    }
    
    // 4-3-3: 1 GK, 4 DEF, 3 MID, 3 FWD
    if (gk === 1 && def === 4 && mid === 3 && fwd === 3) {
      return '4-3-3';
    }
    
    // 3-5-2: 1 GK, 3 DEF, 5 MID, 2 FWD
    if (gk === 1 && def === 3 && mid === 5 && fwd === 2) {
      return '3-5-2';
    }
    
    // 4-2-3-1: 1 GK, 4 DEF, 2 MID, 1 FWD (with wingers or attacking mids)
    if (gk === 1 && def === 4 && mid === 2 && (fwd === 1 || wing === 2)) {
      return '4-2-3-1';
    }
    
    // 3-4-3: 1 GK, 3 DEF, 4 MID, 3 FWD
    if (gk === 1 && def === 3 && mid === 4 && fwd === 3) {
      return '3-4-3';
    }

    // If no exact match, suggest based on player count
    const suggestions = suggestFormationsByPlayerCount(players.length);
    if (suggestions.length > 0) {
      return suggestions[0].formation;
    }
    
    return 'flexible';
  };

  // Get formation for a division
  const getFormationForDivision = (division, divisionIndex) => {
    const divisionKey = divisionIndex.toString();
    // Check if a formation is explicitly selected
    if (selectedFormations[divisionKey] !== undefined && selectedFormations[divisionKey] !== null && selectedFormations[divisionKey] !== '') {
      return selectedFormations[divisionKey];
    }
    // Otherwise auto-detect
    return detectFormation(division.players || []);
  };

  // Calculate player positions using role-based pitch zones (GK → DEF → MID → FWD)
  const calculatePlayerPositions = (players, formation, divisionIndex, vertical = false, goalAtTop = false) => {
    const currentFormation = selectedFormations[divisionIndex.toString()] || formation || 'flexible';
    const customKey = `${divisionIndex}-${currentFormation}`;
    const custom = customPositions[customKey];
    const seed = `${teams?.id || 'team'}-${divisionIndex}-${currentFormation}`;
    const formationMap = vertical ? VERTICAL_FORMATIONS : FORMATIONS;
    let formationTemplate = formationMap[currentFormation];
    if (vertical && !formationTemplate && currentFormation) {
      formationTemplate = buildVerticalFormationTemplate(currentFormation);
    }

    const applyCustom = (player, playerKey, fallback) => {
      if (!vertical && custom && custom[playerKey]) {
        return {
          player,
          x: normalizeStoredX(custom[playerKey].x),
          y: clampCoord(custom[playerKey].y, 8, 92),
        };
      }
      return fallback;
    };

    const hasTemplate =
      currentFormation && currentFormation !== 'flexible' && formationTemplate;

    let positions;

    if (hasTemplate) {
      const formationPositions = formationTemplate;
      const formationSlots = [];
      ['GOALKEEPER', 'DEFENDER', 'MIDFIELDER', 'WINGER', 'FORWARD'].forEach((slotPosition) => {
        (formationPositions[slotPosition] || []).forEach((coord) => {
          formationSlots.push({ slotPosition, coord });
        });
      });

      const { positions: slotted, unassigned } = assignPlayersToFormationSlots(
        players,
        formationSlots,
        { seed }
      );

      const overflow =
        unassigned.length > 0
          ? buildPositionBasedLayout(unassigned, {
              vertical,
              formation: currentFormation,
              seed: `${seed}-overflow`,
            })
          : [];

      positions = [...slotted, ...overflow].map((pos, index) => {
        const playerKey = `${pos.player.id || index}`;
        return vertical ? pos : applyCustom(pos.player, playerKey, pos);
      });
    } else {
      positions = buildPositionBasedLayout(players, {
        vertical,
        formation: currentFormation !== 'flexible' ? currentFormation : null,
        seed,
      }).map((pos, index) => {
        const playerKey = `${pos.player.id || index}`;
        return vertical ? pos : applyCustom(pos.player, playerKey, pos);
      });
    }

    if (vertical) {
      return resolveOverlaps(applyVerticalOrientation(positions, goalAtTop));
    }

    return resolveOverlaps(positions);
  };

  const handlePlayerDrop = (divisionIndex, clientX, clientY, fieldElement) => {
    if (!draggedPlayer || readOnly) return;
    if (draggedPlayer.divisionIndex !== divisionIndex) return;

    const fieldRect = fieldElement.getBoundingClientRect();
    const x = ((clientX - fieldRect.left) / fieldRect.width) * 100;
    const y = ((clientY - fieldRect.top) / fieldRect.height) * 100;

    if (x < -5 || x > 105 || y < -5 || y > 105) return;

    const division = teams.divisions[divisionIndex];
    const formation = getFormationForDivision(division, divisionIndex);
    const currentFormation = selectedFormations[divisionIndex.toString()] || formation || 'flexible';
    const customKey = `${divisionIndex}-${currentFormation}`;
    const playerKey = `${draggedPlayer.playerId}`;

    setCustomPositions((prev) => ({
      ...prev,
      [customKey]: {
        ...(prev[customKey] || {}),
        [playerKey]: {
          x: clampCoord(x, 6, 94),
          y: clampCoord(y, 8, 92),
        },
      },
    }));
    setPositionUpdateCounter((prev) => prev + 1);
    setDraggedPlayer(null);
  };

  const getTeamLabel = (division) => {
    const isRed = division.teamName.includes('Red Team') || division.teamName.includes('🔴');
    const isBlue = division.teamName.includes('Blue Team') || division.teamName.includes('🔵');
    if (isRed) return '🔴 Red Team';
    if (isBlue) return '🔵 Blue Team';
    return division.teamName.replace('🔴 ', '').replace('🔵 ', '');
  };

  const openTeamZoom = (divisionIndex) => {
    if (!zoomableTeams) return;
    setZoomedDivisionIndex(divisionIndex);
  };

  const closeTeamZoom = useCallback(() => {
    setZoomedDivisionIndex(null);
  }, []);

  useEffect(() => {
    if (zoomedDivisionIndex == null) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeTeamZoom();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoomedDivisionIndex, closeTeamZoom]);

  const handleFormationChange = async (divisionIndex, formation) => {
    const formationValue = formation === '' ? null : formation;
    const previousFormation = selectedFormations[divisionIndex.toString()];
    
    // Update state immediately to trigger re-render
    setSelectedFormations(prev => ({
      ...prev,
      [divisionIndex.toString()]: formationValue
    }));

    // Save to backend if teams.id is available
    if (teams && teams.id) {
      try {
        setSavingFormation(true);
        await api.updateTeamFormation(
          teams.id, 
          divisionIndex, 
          formationValue,
          user?.id,
          isAdmin()
        );
      } catch (error) {
        console.error('Failed to save formation:', error);
        // Revert on error
        setSelectedFormations(prev => ({
          ...prev,
          [divisionIndex.toString()]: previousFormation
        }));
      } finally {
        setSavingFormation(false);
      }
    }
  };

  const createFormationGif = useCallback(async () => {
    if (!fieldCaptureRef.current) return;
    setGifGenerating(true);
    const frameIntervalMs = 200;

    const captureFrame = () =>
      new Promise((resolve, reject) => {
        html2canvas(fieldCaptureRef.current, {
          useCORS: true,
          scale: 1.5,
          backgroundColor: null,
          logging: false,
        })
          .then((canvas) => resolve(canvas.toDataURL('image/png')))
          .catch(reject);
      });

    try {
      const dataUrl = await captureFrame();
      const frames = Array(8).fill(dataUrl);

      await new Promise((resolve, reject) => {
        gifshot.createGIF(
          {
            images: frames,
            gifWidth: fieldCaptureRef.current.offsetWidth,
            gifHeight: fieldCaptureRef.current.offsetHeight,
            interval: frameIntervalMs / 1000,
            sampleInterval: 1,
            numWorkers: 2,
          },
          (obj) => {
            if (obj.error) reject(obj.error);
            else {
              const link = document.createElement('a');
              link.download = `formation-gif-${teams?.id || 'field'}-${Date.now()}.gif`;
              link.href = obj.image;
              link.click();
              resolve();
            }
          }
        );
      });
    } catch (e) {
      console.error('Create GIF failed:', e);
    } finally {
      setGifGenerating(false);
    }
  }, [teams?.id]);

  const savePositions = useCallback(async () => {
    if (readOnly) return;

    if (teams?.id && user?.id) {
      try {
        await api.updateTeamFieldPositions(teams.id, customPositions, user.id, isAdmin());
        setPositionsSaved(true);
        setTimeout(() => setPositionsSaved(false), 2500);
        return;
      } catch (e) {
        console.error('Save positions to server failed:', e);
      }
    }

    if (!POSITIONS_STORAGE_KEY) return;
    try {
      localStorage.setItem(POSITIONS_STORAGE_KEY, JSON.stringify(customPositions));
      setPositionsSaved(true);
      setTimeout(() => setPositionsSaved(false), 2500);
    } catch (e) {
      console.error('Save positions failed:', e);
    }
  }, [POSITIONS_STORAGE_KEY, customPositions, readOnly, teams?.id, user?.id, isAdmin]);

  const downloadFormationImage = useCallback(async () => {
    if (!fieldCaptureRef.current) return;
    setImageDownloading(true);
    try {
      const canvas = await html2canvas(fieldCaptureRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: null,
        logging: false
      });
      const link = document.createElement('a');
      link.download = `formation-${teams?.id || 'field'}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error('Download image failed:', e);
    } finally {
      setImageDownloading(false);
    }
  }, [teams?.id]);

  const shareFormationImageViaWhatsApp = useCallback(async () => {
    if (!fieldCaptureRef.current) return;
    setSharingImage(true);
    try {
      const canvas = await html2canvas(fieldCaptureRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: null,
        logging: false
      });
      const dataUrl = canvas.toDataURL('image/png');
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `formation-${teams?.id || 'field'}.png`, { type: 'image/png' });
      const practiceTitle = practice?.title || 'Formation';
      const shareData = {
        title: practiceTitle,
        text: `${practiceTitle} – formation`,
        files: [file]
      };
      if (typeof navigator.share === 'function' && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        const link = document.createElement('a');
        link.download = file.name;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
        const encoded = encodeURIComponent(`${practiceTitle} – formation image. Share the downloaded image in your next message.`);
        window.open(`https://api.whatsapp.com/send?text=${encoded}`, '_blank');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('Share image failed:', e);
    } finally {
      setSharingImage(false);
    }
  }, [teams?.id, practice?.title]);

  if (!teams) {
    return null;
  }

  const vertical = isVerticalLayout(layout);
  const usePhotos = showPlayerPhotos || vertical;

  // Check if divisions exist and have players
  const hasDivisionsWithPlayers = teams.divisions && 
    teams.divisions.length > 0 && 
    teams.divisions.some(div => div.players && div.players.length > 0);

  const renderTeamFieldSection = (division, divisionIndex, { enlarged = false } = {}) => {
    const formation = getFormationForDivision(division, divisionIndex);
    const isRedTeam =
      division.teamName.includes('Red Team') || division.teamName.includes('🔴');
    const teamColor = isRedTeam ? '#dc3545' : '#007bff';
    const currentFormation =
      selectedFormations[divisionIndex.toString()] || formation;
    const goalAtTop = vertical && divisionIndex % 2 === 0;
    const positions = calculatePlayerPositions(
      division.players || [],
      currentFormation,
      divisionIndex,
      vertical,
      goalAtTop
    );
    const customKeyForRender = `${divisionIndex}-${currentFormation}`;
    const positionsHash = customPositions[customKeyForRender]
      ? JSON.stringify(customPositions[customKeyForRender])
      : '';
    const teamLabel = getTeamLabel(division);
    const formationLabel =
      currentFormation && currentFormation !== 'flexible'
        ? ` · ${currentFormation}`
        : '';
    const canZoom = zoomableTeams && !enlarged;

    const handleTeamFieldClick = (event) => {
      if (!canZoom) return;
      if (event.target.closest('.player-marker, .player-marker--linked, a, button')) return;
      openTeamZoom(divisionIndex);
    };

    return (
      <section
        key={`${enlarged ? 'zoom-' : ''}${divisionIndex}-${currentFormation}-${positionsHash}-${positionUpdateCounter}`}
        className={[
          'team-field-section',
          `team-field-section--${isRedTeam ? 'red' : 'blue'}`,
          canZoom ? 'team-field-section--zoomable' : '',
          enlarged ? 'team-field-section--enlarged' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="team-field-label-row">
          <h4 className="team-field-label" style={{ color: teamColor }}>
            {teamLabel}
            {formationLabel}
            <span className="team-field-label__count">
              ({(division.players || []).length} players)
            </span>
          </h4>
          {canZoom && (
            <button
              type="button"
              className="team-field-zoom-trigger"
              onClick={() => openTeamZoom(divisionIndex)}
              aria-label={`Enlarge ${teamLabel} formation`}
            >
              Tap to enlarge
            </button>
          )}
        </div>
        <div
          className={[
            'soccer-field',
            vertical ? 'soccer-field--vertical' : 'soccer-field--half',
            goalAtTop ? 'soccer-field--goal-top' : 'soccer-field--goal-bottom',
            enlarged ? 'soccer-field--enlarged' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-division-index={divisionIndex}
          onClick={canZoom ? handleTeamFieldClick : undefined}
          onKeyDown={
            canZoom
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openTeamZoom(divisionIndex);
                  }
                }
              : undefined
          }
          role={canZoom ? 'button' : undefined}
          tabIndex={canZoom ? 0 : undefined}
          aria-label={canZoom ? `Enlarge ${teamLabel} formation` : undefined}
          onDragOver={
            readOnly
              ? undefined
              : (e) => {
                  e.preventDefault();
                  if (draggedPlayer) e.dataTransfer.dropEffect = 'move';
                }
          }
          onDrop={
            readOnly
              ? undefined
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePlayerDrop(
                    divisionIndex,
                    e.clientX,
                    e.clientY,
                    e.currentTarget
                  );
                }
          }
        >
          <div
            className={[
              'field-background',
              vertical ? 'field-background--vertical' : 'field-background--half',
            ].join(' ')}
          >
            {vertical ? (
              <>
                <div
                  className={`v-field__half-line ${
                    goalAtTop ? 'v-field__half-line--top' : 'v-field__half-line--bottom'
                  }`}
                />
                <div
                  className={`v-field__penalty ${
                    goalAtTop ? 'v-field__penalty--top' : 'v-field__penalty--bottom'
                  }`}
                />
                <div
                  className={`v-field__goal ${
                    goalAtTop ? 'v-field__goal--top' : 'v-field__goal--bottom'
                  }`}
                />
                <div
                  className={`v-field__spot ${
                    goalAtTop ? 'v-field__spot--top' : 'v-field__spot--bottom'
                  }`}
                />
                <div
                  className={`v-field__goal-mouth ${
                    goalAtTop ? 'v-field__goal-mouth--top' : 'v-field__goal-mouth--bottom'
                  }`}
                />
                <div
                  className={`v-field__attack-arrow ${
                    goalAtTop ? 'v-field__attack-arrow--down' : 'v-field__attack-arrow--up'
                  }`}
                  aria-hidden="true"
                >
                  {goalAtTop ? '↓' : '↑'}
                </div>
              </>
            ) : (
              <>
                <div className="half-field-attack-arrow" aria-hidden="true">
                  →
                </div>
                <div className="penalty-box penalty-box-left" />
                <div className="goal-box goal-box-left" />
                <div className="penalty-spot penalty-spot-left" />
                <div className="half-field-line half-field-line--mid" />
              </>
            )}
          </div>

          <div className="field-players-overlay" aria-hidden={positions.length === 0}>
            {positions.map((pos, index) => {
              const isDragging =
                draggedPlayer &&
                draggedPlayer.divisionIndex === divisionIndex &&
                draggedPlayer.playerId === pos.player.id;
              const photoUrl = pos.player.profilePictureUrl
                ? mediaUrl(pos.player.profilePictureUrl)
                : null;
              const profileLink =
                readOnly && getPlayerProfileLink
                  ? getPlayerProfileLink(pos.player)
                  : null;
              const markerClassName = [
                'player-marker',
                usePhotos ? 'player-marker--photo' : 'player-marker--compact',
                profileLink ? 'player-marker--linked' : '',
                enlarged ? 'player-marker--enlarged' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const markerStyle = {
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                ...(usePhotos
                  ? {
                      '--team-accent': teamColor,
                    }
                  : {
                      backgroundColor: teamColor,
                    }),
                animationDelay: `${index * 0.06}s`,
                cursor: profileLink ? 'pointer' : readOnly ? 'default' : 'grab',
                opacity: isDragging ? 0.5 : 1,
              };
              const markerTitle = `${pos.player.name} — ${playerPositionLabel(pos.player)}${pos.player._promotedGoalkeeper ? ' (GK)' : ''}`;
              const markerContent = usePhotos ? (
                <>
                  <div className="player-marker__photo-wrap">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt=""
                        className="player-marker__photo"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="player-marker__photo player-marker__photo--initials"
                        style={{ backgroundColor: teamColor }}
                      >
                        {getPlayerInitials(pos.player.name)}
                      </div>
                    )}
                    {pos.player.skillLevel != null && (
                      <span className="player-marker__rating">
                        {pos.player.skillLevel}
                      </span>
                    )}
                  </div>
                  <div className="player-marker__name">
                    {getPlayerShortName(pos.player.name)}
                  </div>
                  <div className="player-marker__position-abbr">
                    {playerPositionLabel(pos.player)}
                  </div>
                </>
              ) : (
                <>
                  <div className="player-name">
                    {getPlayerShortName(pos.player.name)}
                  </div>
                  <div className="player-position-abbr">
                    {playerPositionLabel(pos.player)}
                  </div>
                </>
              );

              if (profileLink) {
                return (
                  <Link
                    key={pos.player.id || index}
                    to={profileLink}
                    className={markerClassName}
                    style={markerStyle}
                    title={`${markerTitle} — View record`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {markerContent}
                  </Link>
                );
              }

              return (
                <div
                  key={pos.player.id || index}
                  className={markerClassName}
                  draggable={!readOnly}
                  onDragStart={
                    readOnly
                      ? undefined
                      : (e) => {
                          setDraggedPlayer({
                            divisionIndex,
                            playerId: pos.player.id,
                            player: pos.player,
                          });
                          e.dataTransfer.effectAllowed = 'move';
                        }
                  }
                  onDragEnd={
                    readOnly
                      ? undefined
                      : (e) => {
                          if (!draggedPlayer) return;
                          const fieldElement = e.currentTarget.closest(
                            vertical ? '.soccer-field--vertical' : '.soccer-field--half'
                          );
                          if (
                            fieldElement &&
                            Number(fieldElement.dataset.divisionIndex) ===
                              draggedPlayer.divisionIndex
                          ) {
                            handlePlayerDrop(
                              divisionIndex,
                              e.clientX,
                              e.clientY,
                              fieldElement
                            );
                          } else {
                            setDraggedPlayer(null);
                          }
                        }
                  }
                  style={markerStyle}
                  title={markerTitle}
                >
                  {markerContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="soccer-field-visualization">
      <div className="field-visualization-header">
        {!readOnly && !alwaysVisible && (
        <button
          onClick={() => setShowVisualization(!showVisualization)}
          className="btn btn-primary"
          style={{ marginBottom: '1rem' }}
          disabled={!hasDivisionsWithPlayers}
        >
          {showVisualization ? 'Hide' : 'Show'} Field Formation
        </button>
        )}
        {!readOnly && !hasDivisionsWithPlayers && (
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem', fontStyle: 'italic' }}>
            Form teams first to see the field visualization
          </p>
        )}
      </div>

      {showVisualization && hasDivisionsWithPlayers && (
        <div className="field-container">
          <div ref={fieldCaptureRef} className="field-capture-wrapper">
            {practice && (
              <div className="practice-info-overlay">
                <h4>{practice.title}</h4>
                {practice.location && <p>📍 {practice.location}</p>}
                {practice.practiceDate && (
                  <p>📅 {new Date(practice.practiceDate).toLocaleString()}</p>
                )}
              </div>
            )}

            <div
              className={[
                'stacked-fields',
                layout === 'matchup' ? 'stacked-fields--matchup' : '',
                vertical ? 'stacked-fields--vertical' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {teams.divisions.map((division, divisionIndex) =>
                renderTeamFieldSection(division, divisionIndex)
              )}
            </div>
          </div>

          {!readOnly && (
          <div className="formation-export-buttons">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={savePositions}
              title="Save current player positions for this formation"
            >
              {positionsSaved ? '✓ Saved' : 'Save positions'}
            </button>
            <button
              type="button"
              className="btn btn-whatsapp"
              onClick={shareFormationImageViaWhatsApp}
              disabled={sharingImage}
              title="Share formation image via WhatsApp"
            >
              {sharingImage ? 'Preparing…' : '📱 Share image via WhatsApp'}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={downloadFormationImage}
              disabled={imageDownloading}
            >
              {imageDownloading ? 'Downloading…' : 'Download image'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={createFormationGif}
              disabled={gifGenerating}
            >
              {gifGenerating ? 'Creating GIF…' : 'Create GIF'}
            </button>
          </div>
          )}

          {!readOnly && (
          <div className="formation-selectors">
            {teams.divisions.map((division, divisionIndex) => {
              const currentFormation = getFormationForDivision(division, divisionIndex);
              const isRedTeam = division.teamName.includes('Red Team') || division.teamName.includes('🔴');
              const teamLabel = isRedTeam ? '🔴 Red Team' : '🔵 Blue Team';
              const playerCount = (division.players || []).length;
              const suggestions = suggestFormationsByPlayerCount(playerCount);

              return (
                <div key={divisionIndex} className="formation-selector">
                  <label>
                    {teamLabel} Formation ({playerCount} players):
                    <select
                      value={selectedFormations[divisionIndex.toString()] || currentFormation || ''}
                      onChange={(e) => handleFormationChange(divisionIndex, e.target.value)}
                      className="formation-dropdown"
                      disabled={savingFormation}
                    >
                      <option value="">Auto-detect</option>
                      {suggestions.length > 0 ? (
                        suggestions.map((suggestion, idx) => (
                          <option key={idx} value={suggestion.formation}>
                            {suggestion.formation} - {suggestion.description}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="4-4-2">4-4-2 (Classic)</option>
                          <option value="4-3-3">4-3-3 (Attacking)</option>
                          <option value="3-5-2">3-5-2 (Wing Play)</option>
                          <option value="4-2-3-1">4-2-3-1 (Defensive)</option>
                          <option value="3-4-3">3-4-3 (Wide)</option>
                        </>
                      )}
                    </select>
                  </label>
                  <span className="detected-formation">
                    {!selectedFormations[divisionIndex.toString()] && `(Detected: ${currentFormation})`}
                    {suggestions.length > 0 && (
                      <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '0.25rem', color: '#888' }}>
                        💡 Suggested formations for {playerCount} players
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {zoomedDivisionIndex != null && teams.divisions?.[zoomedDivisionIndex] && (
        <div
          className="formation-zoom-overlay"
          onClick={closeTeamZoom}
          role="dialog"
          aria-modal="true"
          aria-label={`${getTeamLabel(teams.divisions[zoomedDivisionIndex])} formation`}
        >
          <div className="formation-zoom-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="formation-zoom-close"
              onClick={closeTeamZoom}
              aria-label="Close enlarged formation"
            >
              ×
            </button>
            {renderTeamFieldSection(
              teams.divisions[zoomedDivisionIndex],
              zoomedDivisionIndex,
              { enlarged: true }
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SoccerFieldVisualization;

